import React, { useState, useCallback, useRef, useEffect, ChangeEvent, DragEvent } from 'react'
import clsx from 'clsx'
import { useForm, Controller } from 'react-hook-form'
import { useMutation } from '@apollo/react-hooks'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { makeStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import CircularProgress from '@material-ui/core/CircularProgress'
import Grid from '@material-ui/core/Grid'
import LinearProgress from '@material-ui/core/LinearProgress'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/Close'
import ProjectImage from '../../../routes/projects/components/project-dashboard/project-image-component'
import useMounted from '@/components/hooks/use-mounted'
import PagTextInput from '@/components/pag-form/pag-text-input'
import { IUploadProjectWithFiles } from '@/shared/models/upload-project-with-files'
import { errorHandler } from '@/shared/error-handler'
import { UPLOAD_PROJECT_WITH_DOCUMENTS } from '@/shared/graphql/mutations/mutationProjects'
import { useAuth0 } from '@/auth/index'
import { callPagApi } from '@/shared/services/pag-api'
import { DefaultFileIcon, FileIcons } from '@/shared/constants/file-icons'
import OptionalFields from './optional-fields'
import { stringToNumber } from '@/utils/numeric'
import { migratedImage } from '@/utils/migrated-image'
import { uploadProjectFile } from '@/shared/services/prodigy-file'
import type { ProjectFileUploadMetadata } from '@/shared/services/prodigy-file'

type UploadStatus = 'uploading' | 'success' | 'error'

type UploadTask = {
  id: string
  filename: string
  progress: number
  status: UploadStatus
  error: string
  uploadedFile: ProjectFileUploadMetadata | null
  isDismissing?: boolean
}

const NumberTypeFields: any = {
  budgetRate: 'float',
  budgetTotal: 'float',
  frAmount: 'float',
}

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 'auto',
    maxWidth: 720,
  },
  fieldsWrapper: {
    maxWidth: 320,
  },
  imageWrapper: {
    width: '100%',
    minWidth: 140,
    maxWidth: 140,
    margin: 'auto',
    '& img': {
      width: '100%',
    },
  },
  descriptionWrapper: {
    minWidth: 148,
  },
  buttonWrapper: {
    marginTop: 32,
  },
  button: {
    height: 32,
    minWidth: 120,
  },
  dropZone: {
    display: 'flex',
    minHeight: 132,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2.5),
    borderRadius: 14,
    border: '2px dashed rgba(34, 80, 153, 0.32)',
    background: '#ffffff',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    cursor: 'pointer',
    width: '100%',
    maxWidth: 320,
    transition: 'border-color 160ms ease, background-color 160ms ease, transform 160ms ease',
    '&:hover': {
      borderColor: '#225099',
      background: '#eef4fb',
    },
  },
  dropZoneActive: {
    borderColor: '#225099',
    background: '#e7f0fb',
    transform: 'translateY(-1px)',
  },
  iconBubble: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: '#225099',
    color: '#ffffff',
    fontSize: '1.5rem',
    fontWeight: 700,
  },
  taskList: {
    display: 'flex',
    maxHeight: 220,
    flexDirection: 'column',
    gap: theme.spacing(1),
    overflowY: 'auto',
    marginTop: theme.spacing(2),
    width: '100%',
    maxWidth: 320,
  },
  taskItem: {
    padding: theme.spacing(1),
    borderRadius: 10,
    background: '#ffffff',
    border: '1px solid rgba(34, 80, 153, 0.14)',
    transition: 'opacity 500ms ease, transform 500ms ease',
  },
  taskItemDismissing: {
    opacity: 0,
    transform: 'translateY(-20px)',
  },
  taskHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  taskStatus: {
    flexShrink: 0,
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  taskUploading: {
    color: '#225099',
  },
  taskError: {
    color: theme.palette.error.main,
  },
  filename: {
    width: '100%',
    color: theme.palette.text.primary,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  progressWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  statusSuccess: {
    color: '#17663a',
    fontWeight: 600,
  },
  error: {
    color: theme.palette.error.main,
  },
  uploadedFilesList: {
    maxHeight: 200,
    overflowY: 'auto',
    width: '100%',
    maxWidth: 320,
    marginTop: theme.spacing(1),
  },
}))

const UploadPortalUploadFilesForm = (props: any) => {
  const { client, project, handleBack, handleStartNewUpload, onUploaded, handleCancel } = props
  const classes = useStyles()
  const { getTokenSilently, getIdTokenClaims, user } = useAuth0()
  const hookFormMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
  })
  const { control, getValues } = hookFormMethods
  const mountedRef = useMounted(true)
  const uploadedFilesRef = useRef<{ filename: string; mimetype: string; url: string; key?: string; source?: string; size?: number; }[]>([])
  const inputRef = useRef<HTMLInputElement | null>(null)
  const uploadControllersRef = useRef<Map<string, AbortController>>(new Map())
  const uploadSequenceRef = useRef(0)
  const commentsRef = useRef('')
  const [uploadedProject, setUploadedProject] = useState(false)
  const [forceUpdate, setForceUpdate] = useState(0)
  const [projectImage, setProjectImage] = useState<string | null>(null)
  const [showOptionalFields, setShowOptionalFields] = useState(false)
  const [tasks, setTasks] = useState<UploadTask[]>([])
  const [isDragging, setIsDragging] = useState(false)

  const [uploadProjectWithDocuments, uploadProjectWithDocumentsRes] = useMutation(
    UPLOAD_PROJECT_WITH_DOCUMENTS,
    {
      onError: (error) => {
        console.log('err =====', error?.networkError, (error.networkError as any)?.statusCode)
        if (error?.networkError) {
          toast.info(`Server is processing now. It'll send you a notification email after done.`)
          setUploadedProject(true)
        } else {
          errorHandler(error)
        }
      },
      onCompleted: (data) => {
        const temp = data?.uploadProjectWithDocuments
        if (temp) {
          toast.success('The documents have been uploaded successfully.')
          setUploadedProject(true)
          onUploaded?.(temp)
        }
      },
    }
  )



  const removeTask = useCallback((id: string) => {
    setTasks((currentTasks) => currentTasks.filter((task) => task.id !== id))
  }, [])

  const resetInput = () => {
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const updateTask = (id: string, patch: Partial<UploadTask>) => {
    setTasks((currentTasks) =>
      currentTasks.map((task) => (task.id === id ? { ...task, ...patch } : task)),
    )
  }

  const uploadFile = async (selectedFile: File) => {
    const id = `${Date.now()}-${uploadSequenceRef.current}`
    uploadSequenceRef.current += 1

    const controller = new AbortController()
    uploadControllersRef.current.set(id, controller)
    setTasks((currentTasks) => [
      ...currentTasks,
      {
        id,
        filename: selectedFile.name,
        progress: 0,
        status: 'uploading',
        error: '',
        uploadedFile: null,
      },
    ])
    setForceUpdate(Date.now())

    try {
      const nextUploadedFile = await uploadProjectFile(selectedFile, {
        getToken: async () => {
          const claims = await getIdTokenClaims()
          if (!claims?.__raw) {
            throw new Error('Unable to acquire Auth0 ID token for file upload')
          }
          return claims.__raw
        },
        signal: controller.signal,
        onProgress: (percentage) => {
          if (!mountedRef.current) return
          updateTask(id, { progress: percentage })
        },
        tenantId: client?.tenantId,
        projectId: project?.id,
        uploadedBy: user?.email || undefined,
        projectNumber: project?.projectNumber,
        brandId: project?.brandId || project?.brand?.id,
        agencyId: project?.agencyId || project?.agency?.id,
      })

      if (!mountedRef.current) return
      updateTask(id, {
        progress: 100,
        status: 'success',
        uploadedFile: nextUploadedFile,
      })

      uploadedFilesRef.current.push({
        filename: nextUploadedFile.filename,
        mimetype: nextUploadedFile.mimetype,
        url: nextUploadedFile.url,
        key: nextUploadedFile.key,
        source: nextUploadedFile.source,
        size: nextUploadedFile.size,
      })
      setForceUpdate(Date.now())

      // Trigger fade out / slide top transition after 2.5 seconds
      setTimeout(() => {
        if (mountedRef.current) {
          updateTask(id, { isDismissing: true })
        }
      }, 2500)

      // Auto-dismiss successful upload after 3 seconds
      setTimeout(() => {
        if (mountedRef.current) {
          removeTask(id)
        }
      }, 3000)
    } catch (uploadError) {
      if (!mountedRef.current) return
      resetInput()
      updateTask(id, {
        progress: 0,
        status: 'error',
        error: uploadError instanceof Error ? uploadError.message : 'Unable to upload file',
      })
      setForceUpdate(Date.now())
    } finally {
      uploadControllersRef.current.delete(id)
    }
  }

  const uploadFiles = async (selectedFiles: FileList | File[]) => {
    const files = Array.from(selectedFiles)
    resetInput()
    files.forEach((selectedFile) => {
      uploadFile(selectedFile)
    })
  }

  const handleFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files

    if (!selectedFiles?.length) {
      return
    }

    await uploadFiles(selectedFiles)
  }

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)

    const droppedFiles = event.dataTransfer.files
    if (droppedFiles?.length) {
      await uploadFiles(droppedFiles)
    }
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
  }

  useEffect(() => {
    return () => {
      mountedRef.current = false
      uploadControllersRef.current.forEach((controller) => controller.abort())
      uploadControllersRef.current.clear()
    }
  }, [mountedRef])

  useEffect(() => {
    if (mountedRef.current) {
      setProjectImage(migratedImage(project, 'image') || null)
    }
  }, [project?.image, project?.imageCf])

  const handleUploadProjectWithFiles = () => {
    if (!client || !project) return
    const abortController = new AbortController()
    try {
      const formValues = getValues()
      Object.keys(NumberTypeFields).forEach((item) => {
        formValues[item] = stringToNumber(formValues[item], NumberTypeFields[item])
      })
      if (formValues['tagIds']?.length) {
        formValues['tagIds'] = formValues['tagIds'].map((item: any) => item.id || item)
      }
      commentsRef.current = formValues.comments || ''

      let payload: IUploadProjectWithFiles

      if (project?.id) {
        payload = {
          tenantId: client.tenantId,
          id: project.id,
          brandId: project.brandId,
          documents: uploadedFilesRef.current,
          image: projectImage,
          ...formValues,
        }
      } else {
        payload = {
          ...project,
          tenantId: client.tenantId,
          clientId: client.id,
          documents: uploadedFilesRef.current,
          image: projectImage,
          ...formValues,
        }
      }

      const callApi = async () => {
        try {
          await callPagApi(
            '',
            getTokenSilently,
            uploadProjectWithDocuments,
            payload,
            abortController.signal
          )
        } catch (e) {
          console.error('uploadProjectWithDocuments failed:', e)
        }
      }
      callApi()
    } catch (err) {
      if (abortController) abortController.abort()
    }
  }

  return (
    <form className={classes.root} name="uploadPortalForm">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item>
              <div className={clsx(classes.imageWrapper, 'pag-project-image-wrapper')}>
                <ProjectImage
                  image={projectImage}
                  project={project}
                  shouldUpdateProject={false}
                  handleCancel={(e: any) => {}}
                  handleSubmit={(e: any) => {
                    setProjectImage(e)
                  }}
                />
              </div>
            </Grid>
            <Grid item xs>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="h5">{project.projectName}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1">
                    {project.client?.name} &nbsp; &gt; &nbsp;
                    {project.brand?.name}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={4}>
                    <Grid item>
                      <div className={classes.descriptionWrapper}>
                        <Typography variant="body1">
                          <b>Project Number : </b> {project.projectNumber}
                        </Typography>
                      </div>
                    </Grid>
                    <Grid item xs>
                      <Typography variant="body1">
                        <span
                          className="pag-blue"
                          onClick={() => setShowOptionalFields(!showOptionalFields)}
                        >
                          {showOptionalFields ? 'Hide Optional Fields' : 'Optional Fields'}
                        </span>
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={4}>
            <Grid item xs={12}></Grid>
            <Grid item xs={6}>
              <div className={classes.fieldsWrapper}>
                {showOptionalFields && (
                  <Grid item xs={12}>
                    <OptionalFields client={client} project={project} control={control} />
                  </Grid>
                )}
                <Grid container spacing={3}>
                  {uploadedProject ? (
                    <Grid item xs={12}>
                      <Typography variant="h5" component="h6" style={{ fontSize: '1.8rem' }}>
                        Summary
                      </Typography>
                    </Grid>
                  ) : (
                    <>
                      <Grid item xs={12}>
                        <Typography
                          variant="h5"
                          component="h6"
                          gutterBottom
                          style={{ fontSize: '1.8rem' }}
                        >
                          Upload Your Documents
                        </Typography>
                        <input
                          ref={inputRef}
                          type="file"
                          multiple
                          onChange={handleFileSelected}
                          style={{ display: 'none' }}
                        />

                        <div
                          className={[classes.dropZone, isDragging ? classes.dropZoneActive : '']
                            .filter(Boolean)
                            .join(' ')}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            inputRef.current?.click()
                          }}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              inputRef.current?.click()
                            }
                          }}
                        >
                          <div className={classes.iconBubble}>+</div>
                          <Typography variant="body1">Drop files here</Typography>
                          <Typography variant="body2">
                            Drag and drop, or click to choose from your computer.
                          </Typography>
                        </div>

                        {tasks.length ? (
                          <div className={classes.taskList}>
                            {tasks.map((task) => (
                              <div
                                key={task.id}
                                className={clsx(
                                  classes.taskItem,
                                  task.isDismissing ? classes.taskItemDismissing : ''
                                )}
                              >
                                <div className={classes.taskHeader}>
                                  <Typography variant="body2" className={classes.filename}>
                                    {task.filename}
                                  </Typography>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Typography
                                      variant="caption"
                                      className={[
                                        classes.taskStatus,
                                        task.status === 'uploading' ? classes.taskUploading : '',
                                        task.status === 'success' ? classes.statusSuccess : '',
                                        task.status === 'error' ? classes.taskError : '',
                                      ]
                                        .filter(Boolean)
                                        .join(' ')}
                                    >
                                      {task.status === 'success' ? 'OK' : task.status}
                                    </Typography>
                                    {task.status === 'error' && (
                                      <IconButton
                                        size="small"
                                        style={{ padding: 2 }}
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          removeTask(task.id)
                                        }}
                                      >
                                        <CloseIcon fontSize="small" style={{ fontSize: '1rem' }} />
                                      </IconButton>
                                    )}
                                  </div>
                                </div>

                                {task.status === 'uploading' ? (
                                  <div className={classes.progressWrap}>
                                    <LinearProgress variant="determinate" value={task.progress} />
                                    <Typography variant="caption">
                                      {Math.round(task.progress)}% uploaded
                                    </Typography>
                                  </div>
                                ) : null}

                                {task.status === 'error' ? (
                                  <Typography variant="caption" className={classes.error}>
                                    {task.error}
                                  </Typography>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </Grid>
                      <Grid item xs={12}>
                        <Controller
                          render={({ field, fieldState, formState }) => (
                            <PagTextInput
                              label="Comments"
                              placeholder="Comments"
                              size="small"
                              variant="filled"
                              inputRef={field.ref}
                              error={fieldState.error}
                              value={field.value}
                              multiline
                              minRows={3}
                              maxRows={6}
                              disabled={uploadProjectWithDocumentsRes.loading}
                              onChange={(e: any) => field.onChange(e.target.value || '')}
                              onBlur={field.onBlur}
                            />
                          )}
                          control={control}
                          name="comments"
                          defaultValue=""
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    {(uploadedFilesRef.current.length > 0 || (uploadedProject && Boolean(commentsRef.current))) && (
                      <div className={classes.uploadedFilesList}>
                        <List disablePadding>
                          {uploadedFilesRef.current.map((f: any, i: number) => (
                            <ListItem key={`uploaded-file-${i}`} dense disableGutters>
                              <ListItemIcon>
                                <FontAwesomeIcon
                                  icon={FileIcons[f.mimetype] || FileIcons[f.type] || DefaultFileIcon}
                                />
                              </ListItemIcon>
                              <ListItemText primary={f.filename || f.name} />
                            </ListItem>
                          ))}
                          {uploadedProject && Boolean(commentsRef.current) && (
                            <ListItem dense disableGutters>
                              <ListItemIcon>
                                <FontAwesomeIcon icon={FileIcons['text/plain'] || DefaultFileIcon} />
                              </ListItemIcon>
                              <ListItemText primary="comments.txt" />
                            </ListItem>
                          )}
                        </List>
                      </div>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <div className={classes.buttonWrapper} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                      <Button
                        color="primary"
                        size="medium"
                        aria-label="cancel"
                        variant="text"
                        className={classes.button}
                        onClick={() => {
                          uploadControllersRef.current.forEach((controller) => controller.abort())
                          uploadControllersRef.current.clear()
                          handleCancel?.()
                        }}
                      >
                        Cancel
                      </Button>
                      {!uploadedProject && (
                        <>
                          <Button
                            color="primary"
                            size="medium"
                            aria-label="back"
                            variant="contained"
                            className={classes.button}
                            disabled={uploadProjectWithDocumentsRes.loading}
                            onClick={() => handleBack()}
                          >
                            BACK
                          </Button>
                          &nbsp;&nbsp;&nbsp;
                        </>
                      )}
                      <Button
                        color="primary"
                        size="medium"
                        aria-label="next"
                        variant="contained"
                        className={classes.button}
                        disabled={
                          uploadProjectWithDocumentsRes.loading ||
                          uploadedFilesRef.current.length === 0 ||
                          tasks.some((task) => task.status === 'uploading')
                        }
                        onClick={() => {
                          if (uploadedProject) {
                            handleStartNewUpload()
                          } else {
                            handleUploadProjectWithFiles()
                          }
                        }}
                      >
                        {uploadProjectWithDocumentsRes.loading ? (
                          <CircularProgress size={14} color="secondary" />
                        ) : uploadedProject ? (
                          'Start New Upload'
                        ) : (
                          'Submit'
                        )}
                      </Button>
                    </div>
                  </Grid>
                </Grid>
              </div>
            </Grid>
            {/* {Boolean(project?.files?.length) && (
              <Grid item xs={6}>
                <Typography variant="h5" component="h6" style={{ fontSize: '1.8rem' }}>
                  Previously Uploaded
                </Typography>
                <List disablePadding>
                  {(project.files || []).map((f: any, i: number) => (
                    <ListItem key={`uploaded-file-${i}`} dense disableGutters>
                      <ListItemIcon>
                        <FontAwesomeIcon icon={FileIcons[f.mimetype] || DefaultFileIcon} />
                      </ListItemIcon>
                      <ListItemText primary={f.filename} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            )} */}
          </Grid>
        </Grid>
      </Grid>
    </form>
  )
}

export default UploadPortalUploadFilesForm
